import { makeStyles } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import EmojiEventsIcon from '@material-ui/icons/EmojiEvents'
import DropdownIcon from '@material-ui/icons/KeyboardArrowDown'
import type { LeaderboardRankResponse } from '@paintscout/api'
import type { DashboardEstimator } from '@paintscout/util/builder'
import { getCalloutContent } from '@paintscout/util/builder'
import classNames from 'classnames'
import { track } from 'shared/util/analytics'
import React, { useEffect, useState } from 'react'
import Collapse from '@paintscout/ui/components/Collapse'
import type { StyleClasses } from '@paintscout/ui/styles'
import Button from '@paintscout/ui/components/Button'
import Typography from '@paintscout/ui/components/Typography'
import { LeaderboardDialog } from '../../dialogs'
import { useDialogs } from '../../providers/DialogStack'
import { useUser } from '../../providers/UserProvider'
import { useUserPermissions } from '../../hooks/useUserPermissions'

const TRANSITION_ALL = 'all 0.3s ease-in-out'

const useStyles = makeStyles<Theme, GamifiedCalloutProps>(
  (theme) => ({
    root: (props) => {
      const isFirefox = typeof navigator !== 'undefined' && navigator.userAgent.toLowerCase().indexOf('firefox') > -1
      const adjustedTopMargin = isFirefox ? (props?.topMargin ?? 3) + 3 : props?.topMargin ?? 3
      return {
        position: 'relative',
        marginTop: theme.spacing(adjustedTopMargin),
        backgroundColor: theme.paintscout.palette.brand.Neutral.P96,
        borderRadius: theme.borderRadius.lg,
        transition: TRANSITION_ALL,
        zIndex: 2,
        ...(props.open
          ? {
              '&:hover': {
                backgroundColor: theme.paintscout.palette.brand.Neutral.P96
              },
              '&:hover $standingsContainerArrow': {
                borderBottom: `${theme.spacing(3)}px solid ${theme.paintscout.palette.brand.Neutral.P96}`
              }
            }
          : {
              '&:hover': {
                backgroundColor: theme.paintscout.palette.brand.Neutral.P94
              },
              '&:hover $standingsContainerArrow': {
                borderBottom: `${theme.spacing(3)}px solid ${theme.paintscout.palette.brand.Neutral.P94}`
              }
            })
      }
    },
    body: {
      backgroundColor: theme.paintscout.palette.brand.Neutral.P96,
      transition: TRANSITION_ALL,
      marginBottom: theme.spacing(1),
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: theme.spacing(1),
      borderRadius: theme.spacing(1),
      position: 'relative',
      zIndex: 3
    },
    standingsContainerArrow: (props) => {
      const { arrowPosition } = props
      return {
        position: 'absolute',
        width: 0,
        height: 0,
        borderLeft: `${theme.spacing(3)}px solid transparent`,
        borderRight: `${theme.spacing(3)}px solid transparent`,
        borderBottom: `${theme.spacing(3)}px solid ${theme.paintscout.palette.brand.Neutral.P96}`,
        top: '-15px',
        left: arrowPosition ?? '12.5%',
        transition: 'all 0.1s ease-in-out',
        zIndex: 1
      }
    },
    standings: {
      display: 'flex',
      padding: theme.spacing(2),
      paddingBottom: 0,
      paddingLeft: theme.spacing(1),
      flexDirection: 'column',
      gap: theme.spacing(1)
    },
    button: {
      display: 'flex',
      padding: theme.spacing(2),
      paddingBottom: theme.spacing(),
      paddingLeft: theme.spacing(1),
      flexDirection: 'column',
      gap: theme.spacing(1)
    },
    standingsContainerBottomBar: {
      width: '100%',
      height: theme.spacing(0.5),
      borderRadius: theme.spacing(1),
      backgroundColor: '#1450B7'
    },
    collapseHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      borderRadius: theme.spacing(1),
      alignItems: 'center',
      zIndex: 2,
      '&:hover': {
        backgroundColor: theme.paintscout.palette.brand.Neutral.P94,
        cursor: 'pointer'
      }
    },
    sneakyHeader: {
      position: 'absolute',
      backgroundColor: 'transparent',
      height: '50px',
      top: 0,
      width: '100%',
      zIndex: 1,
      cursor: 'pointer'
    },
    collapseButton: {
      paddingLeft: theme.spacing(),
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      transition: TRANSITION_ALL,
      paddingTop: theme.spacing(1.6),
      paddingBottom: theme.spacing(1.6),
      fontSize: '0.8rem',
      zIndex: 2,
      '& svg': {
        transition: TRANSITION_ALL,
        marginTop: `-${theme.spacing(0.1)}px`,
        [theme.breakpoints.down('xs')]: {
          marginTop: `0`
        }
      },
      '&:hover': {
        backgroundColor: theme.paintscout.palette.brand.Neutral.P94
      },
      fontWeight: 500
    },
    relative: {
      position: 'relative'
    },
    dropdownIcon: {
      transform: 'rotate(0deg)',
      transition: theme.transitions.create('transform'),
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1.3),
      cursor: 'pointer',
      color: theme.paintscout.palette.brand.Neutral.P60,
      zIndex: 3,
      borderRadius: theme.borderRadius.full,
      '&:hover': {
        backgroundColor: theme.paintscout.palette.brand.Neutral.P94,
        cursor: 'pointer'
      }
    },
    dropdownIconOpen: {
      transform: 'rotate(180deg)'
    },
    hidden: {
      display: 'none'
    }
  }),
  { name: 'GamifiedCallout' }
)

export interface GamifiedCalloutProps {
  classes?: StyleClasses<typeof useStyles>
  type: 'winRate' | 'totalSold'
  data: LeaderboardRankResponse
  onToggleCollapse: (ev?: React.MouseEvent, value?: boolean) => void
  handleJoinClick: () => void
  open: boolean
  selectedEstimators?: DashboardEstimator[]
  allEstimators?: DashboardEstimator[]
  arrowPosition?: string | number
  topMargin?: number
}

function GamifiedCallout(props: GamifiedCalloutProps) {
  const { type, data, open, onToggleCollapse, handleJoinClick, selectedEstimators, allEstimators } = props
  const { user, isSales } = useUser()
  const { permissionToReadAllDocuments } = useUserPermissions()
  const isIndividual = isSales || !permissionToReadAllDocuments
  const userId = user.user_id
  const classes = useStyles(props)
  const leaderboardRankValues = data ? data[type] : { value: null, rank: 0, comment: '', valueChange: 0, rankChange: 0 }
  const { dismissDialog, openDialog } = useDialogs()
  const currentEstimator = selectedEstimators?.[0]
  const userJoinedLeaderboard = user.user_metadata?.hasJoinedLeaderboard

  const [calloutContent, setCalloutContent] = useState(() =>
    getCalloutContent({
      userId,
      userJoinedLeaderboard,
      isSales: isIndividual,
      selectedEstimators,
      allEstimators,
      leaderboardRankValues,
      type
    })
  )

  useEffect(() => {
    setCalloutContent(
      getCalloutContent({
        userId,
        userJoinedLeaderboard,
        isSales: isIndividual,
        selectedEstimators,
        allEstimators,
        leaderboardRankValues,
        type
      })
    )
  }, [leaderboardRankValues, userJoinedLeaderboard, isIndividual])

  const { title, description } = calloutContent

  const handleLeaderboardClick = () => {
    track('leaderboard_viewed', { leaderboardType: type })
    openDialog(LeaderboardDialog, {
      userId: currentEstimator?.value,
      sort: type,
      onClose: () => {
        dismissDialog()
      }
    })
  }

  useEffect(() => {
    if (typeof BroadcastChannel != 'undefined') {
      const channel = new BroadcastChannel('callout')
      const handleMessage = (event: MessageEvent) => {
        if (event.data === 'closed-dialog' || event.data === 'confirmed-dialog') {
          if (!open) {
            onToggleCollapse(null, true)
          }
          setCalloutContent(
            getCalloutContent({
              userId,
              userJoinedLeaderboard: event.data === 'confirmed-dialog',
              isSales: isIndividual,
              selectedEstimators,
              allEstimators,
              leaderboardRankValues,
              type
            })
          )
        }
      }
      channel.addEventListener('message', handleMessage)

      return () => {
        channel.removeEventListener('message', handleMessage)
        channel.close()
      }
    }
  }, [isIndividual])

  return (
    <div className={classes.root}>
      <div className={classes.collapseHeader} onClick={onToggleCollapse}>
        <Button
          className={classNames({
            [classes.collapseButton]: true,
            [classes.hidden]: open
          })}
          variant="text"
        >
          Leaderboard
        </Button>
      </div>
      <Collapse show={open}>
        {open && (
          <div className={classes.body}>
            <div onClick={onToggleCollapse} className={classes.sneakyHeader}></div>
            <div className={classes.standings}>
              <Typography variant="h4">{title}</Typography>
              <Typography variant="body1">{description}</Typography>
            </div>
            <div className={classes.button}>
              <Button
                variant="roundedWhite"
                onClick={userJoinedLeaderboard ? handleLeaderboardClick : handleJoinClick}
                endIcon={<EmojiEventsIcon />}
                id={
                  userJoinedLeaderboard
                    ? type === 'totalSold'
                      ? 'sales-leaderboard-button'
                      : 'win-rate-leaderboard-button'
                    : undefined
                }
              >
                {userJoinedLeaderboard ? 'View Leaderboard' : 'Join Leaderboard'}
              </Button>
            </div>
            <div className={classes.standingsContainerBottomBar} />
          </div>
        )}
      </Collapse>
      <DropdownIcon
        className={classNames({
          [classes.dropdownIcon]: true,
          [classes.dropdownIconOpen]: open
        })}
        onClick={onToggleCollapse}
      />
      <div className={classes.standingsContainerArrow} />
    </div>
  )
}

export default GamifiedCallout
